// Import Validation Service - Conflict detection and dependency validation

import {
  IMPORTABLE_RESOURCE_TYPES,
  WARNING_TYPE,
  QUICK_CONNECT_TYPE
} from './importConstants.js';

/**
 * Normalize a phone number to E.164 format
 * AWS Connect stores phone numbers with the + prefix, but users may enter without it
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - The normalized phone number with + prefix
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber === 'N/A') {
    return phoneNumber;
  }

  // Convert to string in case it's a number (Excel might parse it as numeric)
  const phoneStr = String(phoneNumber).trim();

  // If already has + prefix, return as is
  if (phoneStr.startsWith('+')) {
    return phoneStr;
  }

  // Add + prefix for E.164 format
  return `+${phoneStr}`;
};

/**
 * ImportValidationService class
 * Validates parsed import data against existing Connect resources
 */
class ImportValidationService {
  constructor(existingResources, parsedImportData) {
    this.existingResources = existingResources || {};
    this.parsedData = parsedImportData || {};
    this.validationResults = {};
    this.warnings = [];
  }

  /**
   * Run complete validation
   */
  validate() {
    // Validate each resource type
    IMPORTABLE_RESOURCE_TYPES.forEach(resourceType => {
      this.validateResourceType(resourceType);
    });

    return {
      results: this.validationResults,
      warnings: this.warnings
    };
  }

  /**
   * Validate a specific resource type
   */
  validateResourceType(resourceType) {
    const resources = this.parsedData[resourceType] || [];

    this.validationResults[resourceType] = {
      total: resources.length,
      new: 0,
      existing: 0,
      conflicts: [],
      dependencyWarnings: [],
      resourceDetails: []
    };

    resources.forEach((resource, index) => {
      const resourceName = resource.name;

      // Check for conflicts (pass full resource for user hierarchies to support full path matching)
      const existsInInstance = this.checkConflict(resourceType, resourceName, resource);

      // Validate dependencies
      const dependencyStatus = this.validateDependencies(resourceType, resource);

      const resourceDetail = {
        name: resourceName,
        exists: existsInInstance,
        dependencyStatus: dependencyStatus,
        rowNumber: resource.rowNumber,
        warnings: []
      };

      // Add dependency warnings to resource detail
      if (dependencyStatus.warnings.length > 0) {
        resourceDetail.warnings = dependencyStatus.warnings;
        this.validationResults[resourceType].dependencyWarnings.push(...dependencyStatus.warnings);
      }

      // Count new vs existing
      if (existsInInstance) {
        this.validationResults[resourceType].existing++;
        this.validationResults[resourceType].conflicts.push(resourceName);
      } else {
        this.validationResults[resourceType].new++;
      }

      this.validationResults[resourceType].resourceDetails.push(resourceDetail);
    });
  }

  /**
   * Check if a resource already exists in the instance
   * For user hierarchies, uses full path key to handle duplicate names at different levels
   */
  checkConflict(resourceType, resourceName, resource = null) {
    const resourceMap = this.getResourceMap(resourceType);

    // Special handling for user hierarchies - use full path key
    if (resourceType === 'userHierarchies' && resource && resource.row) {
      const fullPathKey = this.buildHierarchyFullPathKey(resource.row, resource.level);
      return fullPathKey in resourceMap;
    }

    return resourceName in resourceMap;
  }

  /**
   * Build full path key for a hierarchy group from row data
   * e.g., "Automotive|Automotive" for Level 2 "Automotive" under Level 1 "Automotive"
   */
  buildHierarchyFullPathKey(row, level) {
    const parts = [];
    for (let i = 1; i <= level; i++) {
      const levelKey = `level${i}`;
      const value = row[levelKey];
      if (value && value !== 'N/A' && value !== '') {
        parts.push(value);
      }
    }
    return parts.join('|');
  }

  /**
   * Get resource map for a given type
   */
  getResourceMap(resourceType) {
    const mapKey = {
      hoursOfOperation: 'hoursOfOperation',
      agentStatus: 'agentStatus',
      queues: 'queues',
      quickConnects: 'quickConnects',
      routingProfiles: 'routingProfiles',
      userHierarchies: 'userHierarchyGroups',
      phoneNumbers: 'phoneNumbers',
      contactFlows: 'contactFlows',
      users: 'users'
    }[resourceType];

    return this.existingResources[mapKey] || {};
  }

  /**
   * Validate dependencies for a resource
   */
  validateDependencies(resourceType, resource) {
    const warnings = [];
    let allResolved = true;

    switch (resourceType) {
      case 'queues':
        // Validate Hours of Operation dependency
        if (resource.hoursOfOperationName && resource.hoursOfOperationName !== 'N/A') {
          const hoursExists = this.checkResourceExists('hoursOfOperation', resource.hoursOfOperationName);
          if (!hoursExists) {
            warnings.push({
              type: WARNING_TYPE.MISSING_DEPENDENCY,
              message: `Hours of Operation "${resource.hoursOfOperationName}" not found`,
              resourceName: resource.name,
              dependencyType: 'hoursOfOperation',
              dependencyName: resource.hoursOfOperationName
            });
            allResolved = false;
          }
        }

        // Validate Contact Flow reference (warning only, not created during import)
        if (resource.outboundFlowName && resource.outboundFlowName !== 'N/A') {
          const flowExists = this.checkResourceExists('contactFlows', resource.outboundFlowName);
          if (!flowExists) {
            warnings.push({
              type: WARNING_TYPE.REFERENCE_NOT_FOUND,
              message: `Contact Flow "${resource.outboundFlowName}" not found (reference only)`,
              resourceName: resource.name,
              dependencyType: 'contactFlow',
              dependencyName: resource.outboundFlowName
            });
          }
        }

        // Validate Outbound Caller ID Number (reference only, not created during import)
        if (resource.outboundCallerIdNumber && resource.outboundCallerIdNumber !== 'N/A') {
          // Normalize phone number to E.164 format (add + if missing)
          const normalizedPhone = normalizePhoneNumber(resource.outboundCallerIdNumber);
          const phoneExists = this.checkResourceExists('phoneNumbers', normalizedPhone);
          if (!phoneExists) {
            warnings.push({
              type: WARNING_TYPE.REFERENCE_NOT_FOUND,
              message: `Phone Number "${resource.outboundCallerIdNumber}" not found (reference only)`,
              resourceName: resource.name,
              dependencyType: 'phoneNumber',
              dependencyName: resource.outboundCallerIdNumber
            });
          }
        }
        break;

      case 'routingProfiles':
        // Validate default outbound queue
        if (resource.defaultOutboundQueue && resource.defaultOutboundQueue !== 'N/A') {
          const queueExists = this.checkResourceExists('queues', resource.defaultOutboundQueue);
          if (!queueExists) {
            warnings.push({
              type: WARNING_TYPE.MISSING_DEPENDENCY,
              message: `Default Outbound Queue "${resource.defaultOutboundQueue}" not found`,
              resourceName: resource.name,
              dependencyType: 'queue',
              dependencyName: resource.defaultOutboundQueue
            });
            allResolved = false;
          }
        }

        // Validate associated queues
        if (resource.associatedQueues && resource.associatedQueues !== 'N/A') {
          const queueNames = this.parseAssociatedQueueNames(resource.associatedQueues);
          queueNames.forEach(queueName => {
            const queueExists = this.checkResourceExists('queues', queueName);
            if (!queueExists) {
              warnings.push({
                type: WARNING_TYPE.MISSING_DEPENDENCY,
                message: `Associated Queue "${queueName}" not found`,
                resourceName: resource.name,
                dependencyType: 'queue',
                dependencyName: queueName
              });
              allResolved = false;
            }
          });
        }
        break;

      case 'quickConnects':
        const qcType = resource.type ? resource.type.toUpperCase() : 'PHONE_NUMBER';

        if (qcType === QUICK_CONNECT_TYPE.QUEUE) {
          // Validate queue exists
          if (resource.targetName && resource.targetName !== 'N/A') {
            const queueExists = this.checkResourceExists('queues', resource.targetName);
            if (!queueExists) {
              warnings.push({
                type: WARNING_TYPE.MISSING_DEPENDENCY,
                message: `Queue "${resource.targetName}" not found`,
                resourceName: resource.name,
                dependencyType: 'queue',
                dependencyName: resource.targetName
              });
              allResolved = false;
            }
          }

          // Validate contact flow (reference only)
          if (resource.contactFlowName && resource.contactFlowName !== 'N/A') {
            const flowExists = this.checkResourceExists('contactFlows', resource.contactFlowName);
            if (!flowExists) {
              warnings.push({
                type: WARNING_TYPE.REFERENCE_NOT_FOUND,
                message: `Contact Flow "${resource.contactFlowName}" not found (reference only)`,
                resourceName: resource.name,
                dependencyType: 'contactFlow',
                dependencyName: resource.contactFlowName
              });
            }
          }

        } else if (qcType === QUICK_CONNECT_TYPE.USER) {
          // Validate user exists (reference only)
          if (resource.targetName && resource.targetName !== 'N/A') {
            const userExists = this.checkResourceExists('users', resource.targetName);
            if (!userExists) {
              warnings.push({
                type: WARNING_TYPE.REFERENCE_NOT_FOUND,
                message: `User "${resource.targetName}" not found (reference only)`,
                resourceName: resource.name,
                dependencyType: 'user',
                dependencyName: resource.targetName
              });
            }
          }

          // Validate contact flow (reference only)
          if (resource.contactFlowName && resource.contactFlowName !== 'N/A') {
            const flowExists = this.checkResourceExists('contactFlows', resource.contactFlowName);
            if (!flowExists) {
              warnings.push({
                type: WARNING_TYPE.REFERENCE_NOT_FOUND,
                message: `Contact Flow "${resource.contactFlowName}" not found (reference only)`,
                resourceName: resource.name,
                dependencyType: 'contactFlow',
                dependencyName: resource.contactFlowName
              });
            }
          }
        }
        break;

      case 'userHierarchies':
        // User hierarchies are validated during structure building
        // Parent groups must exist before children
        break;

      case 'hoursOfOperation':
      case 'agentStatus':
        // No dependencies
        break;

      default:
        break;
    }

    return {
      allResolved: allResolved,
      warnings: warnings
    };
  }

  /**
   * Check if a resource exists (in instance OR in import set)
   */
  checkResourceExists(resourceType, resourceName) {
    // Check in existing instance resources
    const existsInInstance = this.checkConflict(resourceType, resourceName);
    if (existsInInstance) {
      return true;
    }

    // Check in import data (will be created)
    const importData = this.parsedData[resourceType] || [];
    const existsInImport = importData.some(item => item.name === resourceName);

    return existsInImport;
  }

  /**
   * Parse associated queue names from routing profile string
   * Uses pipe delimiter to support queue names containing commas
   * Supports both formats:
   * - Long format: "Queue Name (Priority: 1, Delay: 0)"
   * - Short format: "Queue Name (P:1, D:0)"
   */
  parseAssociatedQueueNames(associatedQueuesString) {
    if (!associatedQueuesString || associatedQueuesString === 'N/A') {
      return [];
    }

    const queueNames = [];
    // Split on pipe delimiter (with optional surrounding whitespace)
    const queueEntries = associatedQueuesString.split(/\s*\|\s*/).map(s => s.trim());

    queueEntries.forEach(entry => {
      // Try long format: "Queue Name (Priority: 1, Delay: 0)"
      let match = entry.match(/^(.+?)\s*\(Priority:\s*\d+,\s*Delay:\s*\d+\)$/);

      if (!match) {
        // Try short format: "Queue Name (P:1, D:0)" or "Queue Name (P: 1, D: 0)"
        match = entry.match(/^(.+?)\s*\(P:\s*\d+,\s*D:\s*\d+\)$/);
      }

      if (match) {
        queueNames.push(match[1].trim());
      } else if (entry) {
        // Fallback: just the queue name (might not have priority/delay)
        queueNames.push(entry.trim());
      }
    });

    return queueNames;
  }

  /**
   * Get validation summary
   */
  getSummary() {
    const summary = {
      totalResources: 0,
      totalNew: 0,
      totalExisting: 0,
      totalConflicts: 0,
      totalWarnings: 0,
      byResourceType: {}
    };

    Object.keys(this.validationResults).forEach(resourceType => {
      const result = this.validationResults[resourceType];

      summary.totalResources += result.total;
      summary.totalNew += result.new;
      summary.totalExisting += result.existing;
      summary.totalConflicts += result.conflicts.length;
      summary.totalWarnings += result.dependencyWarnings.length;

      summary.byResourceType[resourceType] = {
        total: result.total,
        new: result.new,
        existing: result.existing,
        conflicts: result.conflicts.length,
        warnings: result.dependencyWarnings.length
      };
    });

    return summary;
  }

  /**
   * Get all warnings
   */
  getAllWarnings() {
    const allWarnings = [];

    Object.keys(this.validationResults).forEach(resourceType => {
      const result = this.validationResults[resourceType];
      if (result.dependencyWarnings && result.dependencyWarnings.length > 0) {
        allWarnings.push(...result.dependencyWarnings);
      }
    });

    return allWarnings;
  }

  /**
   * Get conflicts for a specific resource type
   */
  getConflicts(resourceType) {
    return this.validationResults[resourceType]?.conflicts || [];
  }

  /**
   * Get warnings for a specific resource type
   */
  getWarnings(resourceType) {
    return this.validationResults[resourceType]?.dependencyWarnings || [];
  }

  /**
   * Check if validation passed (no critical errors)
   */
  isValid() {
    // Import can proceed even with conflicts (user can choose to skip or update)
    // Only critical errors would prevent import (e.g., file parsing errors)
    return true;
  }
}

export default ImportValidationService;
